/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : appTheme.js
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
const fontTextStyle = "Open Sans";
const placeholderFontSize = "13px";

export const fontStyle = {
  fontFamily: "Open Sans",
  fontStyle: "normal",
  fontSize: "14px",
  lineHeight: "20px",
  lightPrimaryColor: "#000000",
  darkPrimaryColor: "#ffffff",
};

export const fontSizeDetails = {
  h5: {
    fontWeight: 600,
    fontSize: "1.5rem",
  },
  h6: {
    fontWeight: 500,
    fontSize: "1.25rem",
  },
  subtitle1: {
    fontWeight: 500,
    fontSize: "1rem",
  },
  subtitle2: {
    fontWeight: 500,
    fontSize: "0.875rem",
  },
  body1: {
    fontWeight: 400,
    fontSize: ".8rem",
  },
  body2: {
    fontWeight: 400,
    fontSize: "0.7rem",
  },
};

export const appColor = {
  lightBackgroudColor: "#ffffff",
  darkBackgroudColor: "#000000",
};

export const componentStyle = {
  selectStyle: {
    formControl: {
      "& .MuiInputLabel-root": {
        position: "relative",
        transform: "none",
        marginBottom: "4px",
        fontSize: "11px",
        fontWeight: 500,
        color: "rgba(0, 0, 0, 0.6)",
        "&.Mui-error": { color: "#e53935" },
        "&.Mui-disabled": { color: "#bdbdbd" },
        "& .MuiFormLabel-asterisk": {
          color: "red",
        },
      },
      "& .MuiOutlinedInput-root": {
        borderRadius: "0px",
        backgroundColor: "#fff",
        "& fieldset": {
          borderColor: "#d9d9d9", // default
        },
        "&:hover fieldset": {
          borderColor: "#8c8c8c", // hover
        },
        "&.Mui-focused fieldset": {
          borderColor: "#000000", // focused
        },
        "&.Mui-disabled fieldset": {
          borderColor: "#e0e0e0", // disabled
        },
        "&.Mui-error fieldset": {
          borderColor: "#e53935", // error
        },
        "& .MuiSelect-select": {
          paddingY: "10px",
          fontSize: "12px",
          "&::placeholder": {
            color: "#999",
            opacity: 1,
          },
          "&:hover::placeholder": {
            color: "#848587", // dark text on hover
          },
          "&.Mui-focused::placeholder": {
            color: "#232323", // black when focused
          },
        },
      },
      "& .MuiFormHelperText-root.Mui-error": {
        color: "#e53935",
        fontSize: "12px",
        marginLeft: 0,
        marginTop: "4px",
      },
      select: {
        backgroundColor: "#fff",
        "& .MuiSelect-select": {
          paddingY: "10px",
          fontSize: "12px",
        },
      },
      menuItem: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        whiteSpace: "normal",
        wordBreak: "break-word",
        lineHeight: 1.5,
      },
    },
  },

  searchField: {
    textField: {
      "& .MuiOutlinedInput-root": {
        height: "28px", // ✅ overall TextField height
        "& fieldset": { borderColor: "#fff", borderRadius: "0px" },
        "&:hover fieldset": { borderColor: "#97999b" }, // Hover
        "&.Mui-focused fieldset": { borderColor: "#ffffff" }, // Focused
        "&.Mui-disabled fieldset": { borderColor: "#53565A" }, // Disabled
        "&.Mui-error fieldset": { borderColor: "#e53935" }, // Error

        "& .MuiInputBase-input::placeholder": {
          color: "#97999b",
          fontSize: placeholderFontSize,
        },


        "&:hover .MuiInputBase-input::placeholder": {
          color: "#848587",
        },

        "&.Mui-focused .MuiInputBase-input::placeholder": {
          color: "#232323",
        },
      },

      "& .MuiInputLabel-root": {
        color: "#ffffff",
        fontSize: "12px",
        "&.Mui-error": { color: "#e53935" },
        "&.Mui-disabled": { color: "#53565A" },
      },

      "& .MuiInputBase-input": {
        color: "#ffffff", // typed text color
        height: "100%", // ✅ fill available height
        boxSizing: "border-box",
      },

      "& .MuiInputBase-input::placeholder": {
        color: "#ffffff",
        opacity: 1,
      },

      "& .MuiInputAdornment-root": {
        color: "#ffffff",
      },

      "& .MuiFormHelperText-root.Mui-error": {
        color: "#e53935",
        fontSize: "0.8rem",
        marginLeft: 0,
        marginTop: "4px",
      },
    },
  },

  font: {
    fontStyle: fontTextStyle,
    text: {
      fontSize: "14px",
      fontStyle: fontTextStyle,
    },
    tableText: {
      fontSize: "12px",
      fontStyle: fontTextStyle,
    },
    tabLevelHeader: {
      fontSize: "18px",
      fontStyle: fontTextStyle,
      fontWeight: 700,
    },
    cardHeadder: {

      fontSize: "16px",
      fontStyle: fontTextStyle,
      fontWeight: 600,
      overflow: "hidden",
      whiteSpace: "nowrap",
      textOverflow: "ellipsis",
    },
    cardText: {
      fontSize: "14px",
      fontStyle: fontTextStyle,
      fontWeight: 550,
    },
    keyValueHeader: {
      fontSize: "11px",
      fontStyle: fontTextStyle,
      fontWeight: 600,
    },
    keyValueContent: {
      fontSize: "11px",
      fontStyle: fontTextStyle,
    },
    form: {
      textFieldLabel: {
        fontSize: "14px",
        fontStyle: fontTextStyle,
      },
      textFieldValue: {
        fontSize: "14px",
        fontStyle: fontTextStyle,
      },
      textFieldError: {
        fontSize: "11px",
        fontStyle: fontTextStyle,
      },
      textFieldDescription: {
        fontSize: "9px",
        fontStyle: fontTextStyle,
      },
    },
  },
  form: {
    select: {
      label: {
        fontStyle: fontTextStyle,
        fontSize: "14px",
        color: "#6f7174",
        fontWeight: 500,
      },
      labelDarkTheme: {
        color: "white !important", // override
      },
      formControl: {
        "& .MuiInputLabel-root": {
          position: "relative",
          transform: "none",
          marginBottom: "4px",
          fontSize: "0.85rem",
          fontWeight: 500,
          color: "rgba(0, 0, 0, 0.6)",
          "&.Mui-error": { color: "#e53935" },
          "&.Mui-disabled": { color: "#bdbdbd" },
          "& .MuiFormLabel-asterisk": {
            color: "red",
          },
        },
        "& .MuiOutlinedInput-root": {
          borderRadius: "0px",
          backgroundColor: "#fff",
          height: "30px", // ✅ set total height
          "& fieldset": {
            borderColor: "#d9d9d9", // default
          },
          "&:hover fieldset": {
            borderColor: "#8c8c8c", // hover
          },
          "&.Mui-focused fieldset": {
            borderColor: "#000000", // focused
          },
          "&.Mui-disabled fieldset": {
            borderColor: "#e0e0e0", // disabled
          },
          "&.Mui-error fieldset": {
            borderColor: "#e53935", // error
          },
          "&.Mui-selected": {
            backgroundColor: "#FFF3B0 !important", // light yellow
          },
          "& .MuiSelect-select": {
            paddingY: "10px",
            fontSize: "12px",
            "&::placeholder": {
              color: "#999",
              opacity: 1,
            },
            "&:hover::placeholder": {
              color: "#848587", // dark text on hover
            },
            "&.Mui-focused::placeholder": {
              color: "#232323", // black when focused
            },
          },
        },
        "& .MuiFormHelperText-root.Mui-error": {
          color: "#e53935",
          fontSize: "11px",
          marginLeft: 0,
          marginTop: "4px",
        },
      },
      formControlDarkTheme: {
        backgroundColor: "transparent",

        "& .MuiInputLabel-root": {
          color: "white",
        },

        "& .MuiInputLabel-root.Mui-focused": {
          color: "white",
        },
      },
      select: {
        backgroundColor: "#fff",
        "& .MuiSelect-select": {
          paddingY: "10px",
          fontSize: "14px",
        },
      },

      selectDarkTheme: {
        backgroundColor: "transparent !important",

        color: "white",

        "& .MuiSvgIcon-root": {
          color: "white",
        },

        "& .MuiSelect-displayEmpty": {
          color: "white",
          opacity: 0.8,
        },

        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
          borderColor: "white !important",
        },

        "& .MuiOutlinedInput-notchedOutline": {
          borderColor: "white",
        },
        "&:hover .MuiOutlinedInput-notchedOutline": {
          borderColor: "white",
        },
      },
      menuItem: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        whiteSpace: "normal",
        wordBreak: "break-word",
        lineHeight: 1.5,
        fontSize: "14px",
        width: "100%",
        overFlow: "hidden",
        "&.MuiMenuItem-root:hover": {
          backgroundColor: "#e0e0e0 !important", // red
          "&.Mui-selected": {
            backgroundColor: "#ebebeb !important", // yellow
          },
          "&.Mui-selected:hover": {
            backgroundColor: "#e0e0e0 !important", // darker yellow when hovered
          },
        },
      },
      menuItemDartTheme: {
        color: "black", // dropdown text stays readable
      },
    },
  },

  scroll: {
    "&::-webkit-scrollbar": {
      width: "6px",
    },
    "&::-webkit-scrollbar-track": {
      backgroundColor: "transparent",
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "#DDDDDD",
      borderRadius: "3px",
    },
    "&::-webkit-scrollbar-button": {
      display: "none",
      height: 0,
      width: 0,
      background: "transparent",
    },

    scrollbarWidth: "thin",
    scrollbarColor: "#DDDDDD transparent",
  },

  filter: {},
};
